import type { Catalog } from '../types';

/**
 * A volta aberta: detalhe, diálogos de fechamento e o cartão de compartilhamento.
 *
 * Separado de `rounds.*`, que é a **lista**. As duas telas falam da mesma entidade, mas quase não
 * compartilham frase — e juntar tudo num módulo só faria um arquivo que ninguém lê inteiro.
 *
 * A notação de golfe fica como está nos dois idiomas: *Putts*, *GIR*, *Fairway*, *Bunker*,
 * *Penalty*, *Slope*, *Course Rating*, *Score differential*. São termos da regra, não copy — um
 * jogador brasileiro lê "GIR" no cartão de papel também.
 */
export const round = {
  'round.notFound.title': { pt: 'Volta não encontrada', en: 'Round not found' },
  'round.notFound.body': {
    pt: 'Ela pode ter sido removida, ou o link está errado.',
    en: 'It may have been removed, or the link is wrong.',
  },
  'round.notFound.action': { pt: 'Ver todas as voltas', en: 'See every round' },

  'round.heroLabel': { pt: 'Volta', en: 'Round' },
  /** O nome do campo quando o índice ainda não chegou — some assim que a consulta responde. */
  'round.courseFallback': { pt: 'Campo', en: 'Course' },
  'round.meta': {
    pt: '{date} · {holes} buracos · Tee {tee}',
    en: '{date} · {holes} holes · Tee {tee}',
  },
  'round.differential': { pt: 'Differential', en: 'Differential' },

  'round.finishedWithHandicap': {
    pt: 'Volta finalizada · handicap {before} → {after}',
    en: 'Round finished · handicap {before} → {after}',
  },
  'round.finishedRecalculated': {
    pt: 'Volta finalizada · handicap recalculado',
    en: 'Round finished · handicap recalculated',
  },
  'round.finishFailed': { pt: 'Não foi possível finalizar', en: 'Could not finish the round' },
  'round.abandonedToast': { pt: 'Volta abandonada', en: 'Round abandoned' },
  'round.abandonFailed': { pt: 'Não foi possível abandonar', en: 'Could not abandon the round' },

  'round.filled': { pt: 'Preenchidos', en: 'Filled in' },
  'round.filledOf': { pt: 'de {total}', en: 'of {total}' },
  'round.strokesSoFar': { pt: 'Tacadas até aqui', en: 'Strokes so far' },
  'round.toPar': { pt: 'Para o par', en: 'To par' },
  'round.finish': { pt: 'Finalizar volta', en: 'Finish round' },
  'round.keepFilling': { pt: 'Continuar preenchendo', en: 'Keep filling in' },
  'round.closeAs9': { pt: 'Fechar como 9', en: 'Close as 9' },

  'round.note.complete': {
    pt: 'Todos os buracos preenchidos. Finalizar recalcula seu handicap e trava a edição.',
    en: 'Every hole is filled in. Finishing recalculates your handicap and locks the round.',
  },
  'round.note.canClose9': {
    pt: {
      one: 'Falta {count} buraco para finalizar como {holes} — ou feche agora como 9. Voltas não finalizadas não contam para o handicap.',
      other:
        'Faltam {count} buracos para finalizar como {holes} — ou feche agora como 9. Voltas não finalizadas não contam para o handicap.',
    },
    en: {
      one: '{count} hole to go to finish as {holes} — or close it as 9 right now. Unfinished rounds do not count towards your handicap.',
      other:
        '{count} holes to go to finish as {holes} — or close it as 9 right now. Unfinished rounds do not count towards your handicap.',
    },
  },
  'round.note.pending': {
    pt: {
      one: 'Falta {count} buraco para finalizar como {holes}. O servidor só aceita fechar uma volta completa ou com exatamente 9 buracos, então com {scored} preenchidos as saídas são completar ou abandonar. Voltas não finalizadas não contam para o handicap.',
      other:
        'Faltam {count} buracos para finalizar como {holes}. O servidor só aceita fechar uma volta completa ou com exatamente 9 buracos, então com {scored} preenchidos as saídas são completar ou abandonar. Voltas não finalizadas não contam para o handicap.',
    },
    en: {
      one: '{count} hole to go to finish as {holes}. The server only accepts closing a round that is complete or has exactly 9 holes, so with {scored} filled in the ways out are finishing or abandoning. Unfinished rounds do not count towards your handicap.',
      other:
        '{count} holes to go to finish as {holes}. The server only accepts closing a round that is complete or has exactly 9 holes, so with {scored} filled in the ways out are finishing or abandoning. Unfinished rounds do not count towards your handicap.',
    },
  },

  'round.putts': { pt: 'Putts', en: 'Putts' },
  'round.puttsPerHole': { pt: '{value} por buraco', en: '{value} per hole' },
  'round.puttsPerHoleShort': { pt: '{value} / buraco', en: '{value} / hole' },
  'round.gir': { pt: 'GIR', en: 'GIR' },
  'round.fairway': { pt: 'Fairway', en: 'Fairway' },
  'round.fairwayCaption': { pt: '{value} · exclui par 3', en: '{value} · excludes par 3' },
  'round.handicapAtStart': { pt: 'Handicap no início', en: 'Handicap at start' },

  'round.group.title': { pt: 'Volta em grupo', en: 'Group round' },
  'round.group.yours': { pt: 'Sua volta', en: 'Your round' },
  'round.strokes': { pt: 'Tacadas', en: 'Strokes' },
  'round.toParNote': { pt: '{value} ao par', en: '{value} to par' },
  'round.group.counted': { pt: 'Entrou no handicap', en: 'Counted towards handicap' },
  'round.group.scrambleNote': {
    pt: 'No scramble a bola é da dupla, então nenhuma volta entra no handicap — nem a sua.',
    en: 'In a scramble the ball belongs to the team, so no round counts towards a handicap — not even yours.',
  },
  'round.group.matchWhatIf': {
    pt: 'Match play · como ficaria',
    en: 'Match play · how it would stand',
  },

  'round.scorecard.swipe': { pt: 'deslize ↔', en: 'swipe ↔' },
  'round.scorecard.team': { pt: 'Cartão do time', en: 'Team scorecard' },
  'round.scorecard.mine': { pt: 'Meu scorecard', en: 'My scorecard' },
  'round.scorecard.plain': { pt: 'Scorecard', en: 'Scorecard' },

  'round.stats': { pt: 'Estatísticas', en: 'Statistics' },
  'round.distribution': { pt: 'Distribuição de resultados', en: 'Score distribution' },
  'round.holes': { pt: 'Buracos', en: 'Holes' },
  'round.bunker': { pt: 'Bunker', en: 'Bunker' },
  'round.penalty': { pt: 'Penalty', en: 'Penalty' },
  'round.puttsPerHoleLabel': { pt: 'Putts / bur.', en: 'Putts / hole' },
  'round.statsNote': {
    pt: 'GIR conta todos os {holes} buracos. Fairway exclui os par 3 — por isso as bases são diferentes.',
    en: 'GIR counts all {holes} holes. Fairway excludes the par 3s — that is why the bases differ.',
  },

  'round.facts': { pt: 'A volta', en: 'The round' },
  'round.scoreDifferential': { pt: 'Score differential', en: 'Score differential' },
  'round.courseRating': { pt: 'Course Rating', en: 'Course Rating' },
  'round.slope': { pt: 'Slope', en: 'Slope' },
  'round.coursePar': { pt: 'Par do campo', en: 'Course par' },
  'round.factNote': {
    pt: 'Course Rating e Slope ficam congelados no momento em que a volta começou. Se o campo for reavaliado depois, esta volta não muda.',
    en: 'Course Rating and Slope are frozen at the moment the round started. If the course is re-rated later, this round does not change.',
  },

  'round.share': { pt: 'Compartilhar', en: 'Share' },
  'round.viewCourse': { pt: 'Ver campo', en: 'View course' },

  'round.score': { pt: 'Score', en: 'Score' },
  'round.finishDialog.title': { pt: 'Finalizar esta volta?', en: 'Finish this round?' },
  'round.finishDialog.cancel': { pt: 'Voltar ao scorecard', en: 'Back to the scorecard' },
  'round.finishDialog.body': {
    pt: 'Seu handicap será recalculado e a volta não poderá mais ser editada.',
    en: 'Your handicap will be recalculated and this round can no longer be edited.',
  },

  'round.close9.title': { pt: 'Fechar como 9 buracos?', en: 'Close as 9 holes?' },
  'round.close9.confirm': { pt: 'Salvar como 9', en: 'Save as 9' },
  'round.close9.cancel': { pt: 'Continuar os 18', en: 'Keep playing 18' },
  'round.close9.body': {
    pt: {
      one: 'Você preencheu {count} buraco. A volta será salva como 9 buracos e valerá para o handicap.',
      other:
        'Você preencheu {count} buracos. A volta será salva como 9 buracos e valerá para o handicap.',
    },
    en: {
      one: 'You filled in {count} hole. The round will be saved as 9 holes and will count towards your handicap.',
      other:
        'You filled in {count} holes. The round will be saved as 9 holes and will count towards your handicap.',
    },
  },

  'round.abandonDialog.title': { pt: 'Abandonar a volta?', en: 'Abandon the round?' },
  'round.abandonDialog.confirm': { pt: 'Manter a volta', en: 'Keep the round' },
  'round.abandonDialog.body': {
    pt: {
      one: 'O {count} buraco preenchido será descartado e a volta não contará para o handicap.',
      other:
        'Os {count} buracos preenchidos serão descartados e a volta não contará para o handicap.',
    },
    en: {
      one: 'The {count} hole you filled in will be discarded and the round will not count towards your handicap.',
      other:
        'The {count} holes you filled in will be discarded and the round will not count towards your handicap.',
    },
  },
  'round.abandon': { pt: 'Abandonar volta', en: 'Abandon round' },

  // --------------------------------------------------------------- cartão de compartilhamento

  'share.subtitle': {
    pt: 'Sem foto, sem confete: a silhueta do campo é o gráfico. Baixe e poste onde quiser.',
    en: 'No photo, no confetti: the course silhouette is the chart. Download it and post it wherever.',
  },
  'share.close': { pt: 'Fechar', en: 'Close' },
  'share.generating': { pt: 'Gerando…', en: 'Generating…' },
  'share.download': { pt: 'Baixar PNG', en: 'Download PNG' },
  'share.tee': { pt: 'Tee {name}', en: 'Tee {name}' },
  'share.withoutImagery': {
    pt: 'Cartão gerado sem a imagem de satélite',
    en: 'Card generated without the satellite imagery',
  },
  'share.imageFailed': { pt: 'Falha ao gerar a imagem', en: 'Could not generate the image' },
  'share.exportFailed': { pt: 'Falha ao exportar', en: 'Export failed' },
  'share.downloadFailed': {
    pt: 'Não foi possível baixar o cartão',
    en: 'Could not download the card',
  },
  'share.blockedNote': {
    pt: 'A imagem de satélite não pôde ser incorporada (o servidor de imagens não permite exportação), então os cartões saem no verde da marca.',
    en: 'The satellite imagery could not be embedded (the image server does not allow exporting), so the cards come out in the brand green.',
  },
  'share.format.square': { pt: 'Quadrado 1080 × 1080 — feed', en: 'Square 1080 × 1080 — feed' },
  'share.format.story': { pt: 'Story 1080 × 1920 — 9:16', en: 'Story 1080 × 1920 — 9:16' },
  /** Só aparece no nome do arquivo quando o nome do campo não sobra nenhum caractere ASCII. */
  'share.fileFallback': { pt: 'volta', en: 'round' },
} as const satisfies Catalog;
